import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { auth, fmtAmt, card } from '../RealTradePage';

// ─────────────────────────────────────────────
// 지역통계 탭
//   /api/real-trades/bucket-stats  → 평형별 거래량·등락폭
//   /api/real-trades/summary       → 동별 시세, 월별 추이, 거래유형
//
// [등락폭 주의]
//   백엔드가 앞 3개월 / 뒤 3개월을 거래량 가중평균으로 비교해 계산한다.
//   표본이 적으면 reliable=false 가 오므로 화면에 경고를 표시한다.
// ─────────────────────────────────────────────

export default function RegionStats({ lawdCd, zone, from, to, regions, propType }) {
  const [buckets, setBuckets] = useState([]);
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!lawdCd && !zone) return;
    setLoading(true);
    try {
      // 권역(zone) 또는 시군구(lawd_cd) 중 하나로 조회한다
      const qs = new URLSearchParams({ from, to, prop_type: propType });
      if (lawdCd) qs.set('lawd_cd', lawdCd);
      else if (zone) qs.set('zone', zone);
      const q = qs.toString();
      // 두 API를 동시에 호출해 대기시간을 줄인다
      const [b, s] = await Promise.all([
        axios.get(`/api/real-trades/bucket-stats?${q}`, auth()),
        axios.get(`/api/real-trades/summary?${q}`, auth()),
      ]);
      setBuckets(b.data.buckets || []);
      setSum(s.data);
    } catch (e) {
      alert('조회 실패: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [lawdCd, zone, from, to, propType]);

  useEffect(() => { load(); }, [load]);

  const sggName = zone || regions.find(r => r.lawd_cd === lawdCd)?.sgg_nm || '';

  // 월별 거래량 그래프용 (YYYYMM → 26.01)
  const monthData = (sum?.by_month || []).map(m => ({
    ym: `${m.deal_ym.slice(2,4)}.${m.deal_ym.slice(4,6)}`,
    거래량: m.cnt,
    평당가: m.avg_unit,
  }));

  if (loading) return <div style={{ padding: 20, color: '#3b82f6' }}>조회 중…</div>;
  if (!sum) return null;

  return (
    <div>
      {/* ── 1. 평형별 거래량 · 등락폭 ── */}
      <div style={card}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>
          평형별 거래량 · 등락폭
          <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
            {sggName} · {from}~{to}
          </span>
        </h3>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              {['평형 구간','거래건수','기준 평당가','최근 평당가','등락폭','신뢰도']
                .map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {buckets.map(b => (
              <tr key={b.bucket} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={td}>{b.label}</td>
                <td style={tdR}>{b.total_cnt.toLocaleString()}건</td>
                <td style={tdR}>{b.head_unit?.toLocaleString() ?? '-'}</td>
                <td style={tdR}>{b.tail_unit?.toLocaleString() ?? '-'}</td>
                <td style={{ ...tdR, fontWeight: 700,
                  color: b.change_pct > 0 ? '#dc2626'
                       : b.change_pct < 0 ? '#2563eb' : '#64748b' }}>
                  {b.change_pct == null ? '-'
                    : `${b.change_pct > 0 ? '▲' : b.change_pct < 0 ? '▼' : '─'} ${Math.abs(b.change_pct)}%`}
                </td>
                <td style={td}>
                  {b.reliable
                    ? <span style={badgeOk}>충분</span>
                    : <span style={badgeWarn}>표본부족</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p style={{ margin: '10px 0 0', fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>
          ※ 등락폭은 <b>앞 3개월 평균</b>과 <b>뒤 3개월 평균</b>의 평당가를 거래량 가중평균으로 비교한 값입니다.
          양 끝 1개월만 비교하면 그 달의 거래 구성에 따라 값이 크게 흔들리기 때문입니다.<br/>
          ※ "표본부족"은 거래 100건 미만 또는 6개월 미만인 경우로, 등락폭을 참고용으로만 보셔야 합니다.
        </p>
      </div>

      {/* ── 2. 월별 거래량 + 평당가 추이 ── */}
      <div style={card}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>월별 거래량 · 평당가 추이</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={monthData} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="ym" tick={{ fontSize: 12 }} />
            {/* 거래량과 평당가는 단위가 달라 Y축을 좌우로 분리한다 */}
            <YAxis yAxisId="L" tick={{ fontSize: 11 }} width={45} />
            <YAxis yAxisId="R" orientation="right" tick={{ fontSize: 11 }} width={55}
                   tickFormatter={v => (v / 1000).toFixed(1) + '천'} />
            <Tooltip formatter={(v, name) =>
              name === '거래량' ? [v + '건', name] : [v.toLocaleString() + '만원/평', name]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line yAxisId="L" type="monotone" dataKey="거래량"
                  stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} />
            <Line yAxisId="R" type="monotone" dataKey="평당가"
                  stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── 3. 법정동별 시세 ── */}
      <div style={card}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>법정동별 거래량 · 시세</h3>
        <ResponsiveContainer width="100%" height={Math.max(200, (sum.by_umd?.length || 0) * 28)}>
          <BarChart data={sum.by_umd} layout="vertical"
                    margin={{ top: 5, right: 30, bottom: 5, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="umd_nm" width={80} tick={{ fontSize: 12 }} />
            <Tooltip formatter={v => [v + '건', '거래량']} />
            <Bar dataKey="cnt" fill="#3b82f6" radius={[0, 3, 3, 0]} />
          </BarChart>
        </ResponsiveContainer>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 12 }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              {['법정동','거래량','평균가','평당가','최고가'].map(h =>
                <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {sum.by_umd?.map(u => (
              <tr key={u.umd_nm} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={td}>{u.umd_nm}</td>
                <td style={tdR}>{u.cnt}건</td>
                <td style={tdR}>{fmtAmt(u.avg_amt)}</td>
                <td style={{ ...tdR, fontWeight: 600 }}>{u.avg_unit?.toLocaleString()}</td>
                <td style={{ ...tdR, color: '#dc2626' }}>{fmtAmt(u.max_amt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ margin: '10px 0 0', fontSize: 11, color: '#94a3b8' }}>
          ※ 평균가가 낮아도 평당가가 높을 수 있습니다(소형 위주 지역). 두 값을 함께 보세요.
        </p>
      </div>

      {/* ── 4. 거래 형태 분석 ── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ ...card, flex: '1 1 240px', minWidth: 0 }}>
          <h4 style={h4}>거래 유형</h4>
          {sum.by_dealing?.map(d => (
            <Row key={d.gbn} label={d.gbn} value={`${d.cnt}건`} />
          ))}
        </div>

        <div style={{ ...card, flex: '1 1 240px', minWidth: 0 }}>
          <h4 style={h4}>매수자 유형</h4>
          {sum.by_buyer?.map(b => (
            <Row key={b.buyer} label={b.buyer} value={`${b.cnt}건`} />
          ))}
        </div>

        <div style={{ ...card, flex: '1 1 300px', minWidth: 0 }}>
          <h4 style={h4}>중개사 소재지 (상위 8)</h4>
          {sum.by_agent?.slice(0, 8).map(a => (
            <Row key={a.agent_sgg} label={a.agent_sgg} value={`${a.cnt}건`} />
          ))}
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
            {sum.note}
          </p>
        </div>
      </div>

      {/* ── 5. 거래량 상위 단지 ── */}
      <div style={card}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>거래량 상위 단지</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              {['단지명','법정동','거래량','평균가','평당가'].map(h =>
                <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {sum.top_apt?.map((a, i) => (
              <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={{ ...td, fontWeight: 600 }}>{a.apt_nm}</td>
                <td style={td}>{a.umd_nm}</td>
                <td style={tdR}>{a.cnt}건</td>
                <td style={tdR}>{fmtAmt(a.avg_amt)}</td>
                <td style={{ ...tdR, fontWeight: 600 }}>{a.avg_unit?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 라벨-값 한 줄 (거래유형/매수자/중개사 카드에서 공용)
function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between',
                  padding: '5px 0', borderBottom: '1px solid #f8fafc', fontSize: 13 }}>
      <span style={{ color: '#475569' }}>{label}</span>
      <b>{value}</b>
    </div>
  );
}

const th  = { padding: '8px', textAlign: 'left', fontWeight: 600,
              color: '#475569', whiteSpace: 'nowrap' };
const td  = { padding: '8px', whiteSpace: 'nowrap' };
const tdR = { ...td, textAlign: 'right' };
const h4  = { margin: '0 0 8px', fontSize: 14 };
const badgeOk   = { fontSize: 11, padding: '2px 8px', borderRadius: 10,
                    background: '#dcfce7', color: '#166534' };
const badgeWarn = { fontSize: 11, padding: '2px 8px', borderRadius: 10,
                    background: '#fef3c7', color: '#92400e' };